import { useState, useEffect } from 'react'
import H from '../data'
import HI from '../components/Icons'
import { Av, Stars, StatusBadge, Delta, Kpi, Banner, Empty, SkeletonRows, BarChart, Sparkline, CardHead, money } from '../components/SharedComponents'

/* ============ CoCafe Host — Pricing ============ */
function Pricing({ toast }) {
  const [sessions, setSessions] = useState(H.pricing.sessions);
  const [addons, setAddons] = useState(H.pricing.addons);
  const [surcharge, setSurcharge] = useState(H.pricing.weekendSurcharge);
  const [promos, setPromos] = useState(H.pricing.promos);
  const [dirty, setDirty] = useState(false);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 650);
    return () => clearTimeout(t);
  }, []);

  const setPrice = (key, v) => { setSessions((s) => s.map((x) => x.key === key ? { ...x, price: Math.max(0, v) } : x)); setDirty(true); };
  const toggleAddon = (key) => { setAddons((a) => a.map((x) => x.key === key ? { ...x, on: !x.on } : x)); setDirty(true); };

  if (loading) {
    return (
      <div className="page fade-in">
        <div className="row g-4">
          <div className="col-12 col-lg-8">
            <div className="card" style={{ marginBottom: 20, height: 250 }}>
              <div style={{ padding: 16 }}>
                <SkeletonRows rows={4} cols={2} />
              </div>
            </div>
            <div className="card" style={{ height: 200 }}>
              <div style={{ padding: 16 }}>
                <SkeletonRows rows={3} cols={2} />
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-4">
            <div className="card" style={{ marginBottom: 20, height: 200 }}>
              <div style={{ padding: 16 }}>
                <SkeletonRows rows={3} cols={1} />
              </div>
            </div>
            <div className="card" style={{ height: 200 }}>
              <div style={{ padding: 16 }}>
                <SkeletonRows rows={2} cols={1} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page fade-in">

      {/* 2x2 GRID LAYOUT */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
        {/* TOP-LEFT: Session Rates */}
        <div className="card" style={{ maxWidth: '450px', width: '100%' }}>
          <CardHead title="Session rates" sub="Price per desk" />
          <div style={{ padding: 6, display: 'flex', flexDirection: 'column', gap: 0 }}>
            {sessions.map((s, i) => (
              <div key={s.key} style={{ padding: "10px 12px", borderBottom: i < sessions.length - 1 ? "1px solid var(--border)" : "none", display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', textAlign: 'center' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '12px' }}>{s.label}</div>
                  <div style={{ fontSize: '9px', color: 'var(--muted)' }}>{s.key === "day" ? "Full-day" : "Per " + s.label.toLowerCase()}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                  <div className="inp-affix" style={{ width: '95px' }}>
                    <span className="pre">₹</span>
                    <input type="number" value={s.price} onChange={(e) => setPrice(s.key, parseInt(e.target.value || 0))} style={{ fontSize: '14px', padding: '4px 6px', textAlign: 'center' }} />
                  </div>
                  <button className="btn btn-soft btn-icon btn-sm" onClick={() => setPrice(s.key, s.price - 10)} style={{ padding: '3px 5px' }}><HI.minus s={10} /></button>
                  <button className="btn btn-soft btn-icon btn-sm" onClick={() => setPrice(s.key, s.price + 10)} style={{ padding: '3px 5px' }}><HI.plus s={10} /></button>
                </div>
              </div>
            ))}
          </div>
          <div className="card-foot row between" style={{ padding: '8px 10px', fontSize: '11px' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '11px' }}>Weekend</div>
              <div style={{ fontSize: '9px', color: 'var(--muted)' }}>Sat & Sun</div>
            </div>
            <div className="row g4">
              {[0, 10, 15, 20].map((v) => (
                <button key={v} className={"chip" + (surcharge === v ? " sel" : "")} onClick={() => { setSurcharge(v); setDirty(true); }} style={{ cursor: "pointer", padding: '2px 6px', fontSize: '9px' }}>+{v}%</button>
              ))}
            </div>
          </div>
        </div>

        {/* TOP-RIGHT: What Guests See */}
        <div className="card" style={{ maxWidth: '450px', width: '100%', display: 'flex', flexDirection: 'column' }}>
          <CardHead title="What guests see" />
          <div style={{ padding: '16px 12px', textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ fontSize: '12px', color: 'var(--muted)', fontWeight: 600, marginBottom: 12 }}>Loftworks Coffee · from</div>
            <div style={{ fontSize: '36px', fontWeight: 800, marginBottom: 14, lineHeight: 1, letterSpacing: '-.02em' }}>{money(sessions[0].price)}<span style={{ fontSize: '14px', color: 'var(--muted)', fontWeight: 600, marginLeft: '6px' }}>/ hour</span></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '11px', marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
              {sessions.map((s) => (
                <div key={s.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: 'var(--muted)' }}>{s.label}</span>
                  <span className="mono" style={{ fontWeight: 600 }}>{money(s.price)}</span>
                </div>
              ))}
            </div>
            {surcharge > 0 && <div style={{ fontSize: '10px', color: 'var(--muted)', marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border)" }}>+{surcharge}% weekends</div>}
          </div>
        </div>

        {/* BOTTOM-LEFT: Add-ons */}
        <div className="card" style={{ maxWidth: '450px', width: '100%' }}>
          <CardHead title="Add-ons" sub="Optional extras" />
          <div style={{ padding: 6, maxHeight: '300px', overflowY: 'auto' }}>
            {addons.map((a, i) => (
              <div key={a.key} style={{ padding: "10px 12px", borderBottom: i < addons.length - 1 ? "1px solid var(--border)" : "none", display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', textAlign: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                  <input
                    type="checkbox"
                    checked={a.on}
                    onChange={() => toggleAddon(a.key)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: 'var(--green)' }}
                  />
                  <div>
                    <div style={{ fontWeight: 650, color: a.on ? "var(--ink)" : "var(--muted)", fontSize: '11px' }}>{a.label}</div>
                    <div style={{ fontSize: '8px', color: 'var(--muted)' }}>{a.unit}</div>
                  </div>
                </div>
                <div className="inp-affix" style={{ width: '95px', opacity: a.on ? 1 : .5 }}>
                  <span className="pre">₹</span>
                  <input type="number" value={a.price} disabled={!a.on} onChange={(e) => { setAddons((x) => x.map((y) => y.key === a.key ? { ...y, price: parseInt(e.target.value || 0) } : y)); setDirty(true); }} style={{ fontSize: '14px', padding: '4px 6px', textAlign: 'center' }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* BOTTOM-RIGHT: Offers & Promos */}
        <div className="card" style={{ maxWidth: '450px', width: '100%' }}>
          <CardHead title="Offers & promos" right={<button className="btn btn-soft btn-sm" onClick={() => setCreating(true)} style={{ padding: '2px 5px', fontSize: '10px' }}><HI.plus s={10} /></button>} />
          <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '300px', overflowY: 'auto' }}>
            {promos.map((p) => (
              <div key={p.code} style={{ padding: 10, border: "1px solid var(--border)", borderRadius: "var(--r-sm)", opacity: p.status === "ended" ? .6 : 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '5px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center', width: '100%', minWidth: 0 }}>
                  <span style={{ color: "var(--clay)", flexShrink: 0 }}><HI.megaphone s={11} /></span>
                  <span className="mono" style={{ fontWeight: 700, fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.code}</span>
                  {p.status === "active" ? <span className="badge green" style={{ fontSize: '9px', padding: '2px 4px', flexShrink: 0 }}><span className="bd" />Active</span> : <span className="badge gray" style={{ fontSize: '9px', padding: '2px 4px', flexShrink: 0 }}>Ended</span>}
                </div>
                <div style={{ lineHeight: 1.3, fontSize: '9px', color: 'var(--muted)', maxWidth: '100%', wordBreak: 'break-word' }}>{p.desc}</div>
                <div style={{ fontSize: '9px', color: 'var(--muted)', width: '100%' }}>
                  {p.uses}{p.cap ? " / " + p.cap : ""}
                </div>
                {p.status === "active" && <button className="btn btn-soft btn-sm" style={{ fontSize: '9px', padding: '3px 8px', minWidth: 0 }} onClick={() => { setPromos((x) => x.map((y) => y.code === p.code ? { ...y, status: "ended", ends: "Ended today" } : y)); toast("Promo paused", p.code + " is no longer active"); }}>End</button>}
                {p.cap && p.status === "active" && <div className="meter" style={{ marginTop: 5, width: '100%' }}><span style={{ width: (p.uses / p.cap * 100) + "%" }} /></div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {creating && (
        <div className="modal">
          <div className="scrim" onClick={() => setCreating(false)} />
          <div className="modal-card">
            <div className="modal-b">
              <div className="t-h2" style={{ marginBottom: 14 }}>Create a promo</div>
              <div className="col g14">
                <div className="field"><span className="lbl">Promo code</span><input className="inp mono" placeholder="HAPPYHOUR" defaultValue="HAPPYHOUR" /></div>
                <div className="row g12">
                  <div className="field grow"><span className="lbl">Discount</span>
                    <select className="sel"><option>15% off</option><option>20% off</option><option>₹100 off</option></select></div>
                  <div className="field grow"><span className="lbl">Applies to</span>
                    <select className="sel"><option>Evening slots (after 6pm)</option><option>All sessions</option><option>Day passes</option></select></div>
                </div>
                <div className="field"><span className="lbl">Usage cap (optional)</span><input className="inp" type="number" placeholder="100" /></div>
              </div>
            </div>
            <div className="modal-f">
              <button className="btn btn-ghost" onClick={() => setCreating(false)}>Cancel</button>
              <button className="btn btn-pri" onClick={() => { setPromos((x) => [{ code: "HAPPYHOUR", desc: "15% off evening slots after 6pm", value: "15%", uses: 0, cap: 100, status: "active", ends: "31 Jul" }, ...x]); setCreating(false); toast("Promo created", "HAPPYHOUR is now live"); }}>Create promo</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Pricing;
